/**
 * 项目创建向导 — 两步走：标段识别 → 项目信息提取
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { projectApi } from '../services/projectApi';
import api from '../services/api';
import { toast } from '../store/toastStore';
import { DOCUMENT_ACCEPT, isSupportedDocumentFile } from '../utils/fileTypes';
import { CheckCircleIcon, ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';

interface ModelConfig {
  id: number;
  model_name: string;
  provider: string;
  is_default: boolean;
}

type WizardStep = 'select_type' | 'form';


/** 从项目规模文本中提取预算金额（万元） */
function extractBudgetFromText(text: string): number {
  if (!text) return 0;
  // 匹配 "X万元" 或 "X万" 模式
  const match = text.match(/(\d[\d,.]*)\s*万\s*(?:元)?/);
  if (match) {
    return parseFloat(match[1].replace(/,/g, ''));
  }
  return 0;
}

const ProjectWizard: React.FC = () => {
  const navigate = useNavigate();
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(-1);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');

  // 两步走状态
  const [wizardStep, setWizardStep] = useState<WizardStep>('select_type');
  const [documentType, setDocumentType] = useState<'bid' | 'proposal'>('bid');
  const [proposalMode, setProposalMode] = useState<'normal' | 'concise'>('normal');
  const [packages, setPackages] = useState<any[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
  const [packagesChecked, setPackagesChecked] = useState(false);  // track if analyze-packages completed

  const [formData, setFormData] = useState({
    project_name: '',
    file_content: '',
    project_overview: '',
    project_background: '',
    project_scale: '',
    project_schedule: '',
    project_content: '',
    technical_features: '',
    other_requirements: '',
    tech_requirements: '',
    business_requirements: '',
    price_requirements: '',
    project_amount: 0,
    model_config_id: 0,
    expected_word_count: 300000,
  });

  useEffect(() => { loadModels(); }, []);

  const loadModels = async () => {
    try {
      const response = await api.get('/api/models');
      const modelList = response.data.models || [];
      setModels(modelList);
      const defaultModel = modelList.find((m: ModelConfig) => m.is_default);
      if (defaultModel) {
        setFormData(prev => ({ ...prev, model_config_id: defaultModel.id }));
      } else if (modelList.length > 0) {
        setFormData(prev => ({ ...prev, model_config_id: modelList[0].id }));
      }
    } catch (error) {
      console.error('加载模型配置失败:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!isSupportedDocumentFile(file)) { toast.warning(`文件 ${file.name} 格式不支持`); continue; }
      if (file.size > 100 * 1024 * 1024) { toast.warning(`文件 ${file.name} 超过 100MB`); continue; }
      newFiles.push(file);
    }
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fillFormWithPackage = (pkg: any) => {
    let mergedOverview = '';
    if (pkg.project_overview) mergedOverview += `【项目基本信息】\n${pkg.project_overview}\n\n`;
    if (pkg.project_background) mergedOverview += `【项目背景和目的】\n${pkg.project_background}\n\n`;
    if (pkg.project_scale) mergedOverview += `【项目规模和预算】\n${pkg.project_scale}\n\n`;
    if (pkg.project_schedule) mergedOverview += `【项目时间安排】\n${pkg.project_schedule}\n\n`;
    if (pkg.project_content) mergedOverview += `【项目要实施的具体内容】\n${pkg.project_content}\n\n`;
    if (pkg.technical_features) mergedOverview += `【主要技术特点】\n${pkg.technical_features}\n\n`;
    if (pkg.other_requirements) mergedOverview += `【其他关键要求】\n${pkg.other_requirements}`;

    if (documentType === 'proposal') {
      // 方案：合并非功能性需求和验收标准到概述，不填充评分项
      if (pkg.non_functional_requirements) mergedOverview += `\n\n【非功能性需求】\n${pkg.non_functional_requirements}`;
      if (pkg.acceptance_criteria) mergedOverview += `\n\n【建设目标与验收标准】\n${pkg.acceptance_criteria}`;
      setFormData(prev => ({
        ...prev,
        project_name: pkg.project_name || '',
        project_overview: mergedOverview.trim(),
        tech_requirements: '',
        business_requirements: typeof pkg.business_requirements === 'object' ? JSON.stringify(pkg.business_requirements) : (pkg.business_requirements || ''),
        price_requirements: '',
      }));

      // Extract budget amount from project_scale for auto word count
      const budgetText = pkg.project_scale || '';
      const extracted = extractBudgetFromText(budgetText);
      if (extracted > 0) {
        const calculated = extracted >= 500 ? extracted * 1000 : 300000;
        setFormData(prev => ({
          ...prev,
          project_amount: extracted,
          expected_word_count: calculated,
        }));
      }
    } else {
      // 已按 documentType 区分处理
      setFormData(prev => ({
        ...prev,
        project_name: pkg.project_name || '',
        project_overview: mergedOverview.trim(),
        tech_requirements: typeof pkg.tech_requirements === 'object' ? JSON.stringify(pkg.tech_requirements) : (pkg.tech_requirements || ''),
        business_requirements: typeof pkg.business_requirements === 'object' ? JSON.stringify(pkg.business_requirements) : (pkg.business_requirements || ''),
        price_requirements: typeof pkg.price_requirements === 'object' ? JSON.stringify(pkg.price_requirements) : (pkg.price_requirements || ''),
      }));

      // Extract budget amount from project_scale for auto word count
      const budgetText = pkg.project_scale || '';
      const extracted = extractBudgetFromText(budgetText);
      if (extracted > 0) {
        const calculated = extracted >= 500 ? extracted * 1000 : 300000;
        setFormData(prev => ({
          ...prev,
          project_amount: extracted,
          expected_word_count: calculated,
        }));
      }
    }
  };

  /** 上传文件 → 调 analyze-packages → 进入标段选择 */
  const handleUpload = async () => {
    if (uploadedFiles.length === 0) { toast.warning('请先选择文件'); return; }
    if (models.length === 0) { toast.warning('未配置模型，无法自动分析。请先在设置中配置模型。'); return; }

    setUploading(true);
    const contents: string[] = [];
    let failed = false;
    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      setUploadingIndex(i);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/api/document/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.success) {
          contents.push(`【${file.name}】\n${res.data.file_content}`);
        } else { failed = true; break; }
      } catch { failed = true; break; }
    }
    setUploading(false);
    setUploadingIndex(-1);
    if (failed) return;

    const merged = contents.join('\n\n---\n\n');
    setFormData(prev => ({ ...prev, file_content: merged }));

    // 方案类型直接跳过标段识别
    if (documentType === 'proposal') {
      setAnalyzing(true);
      setAnalysisProgress('正在提取项目信息...');
      await fetchProjectInfo(merged, null);
      return;
    }

    // 调 analyze-packages 识别标段
    setAnalyzing(true);
    setAnalysisProgress('AI 正在识别标段...');
    const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_BASE_URL}/api/document/analyze-packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ file_content: merged, document_type: documentType }),
      });

      let pkgList: any[] = [];
      let gotResult = false;

      if (response.ok) {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            for (const line of chunk.split('\n')) {
              if (!line.startsWith('data: ')) continue;
              const data = line.substring(6).trim();
              if (data === '[DONE]') continue;
              try {
                const evt = JSON.parse(data);
                if (evt.type === 'packages_result' && Array.isArray(evt.packages)) {
                  pkgList = evt.packages;
                  gotResult = true;
                } else if (evt.type === 'error') {
                  throw new Error(evt.message || '标段识别失败');
                }
              } catch (e: any) {
                if (e.message?.includes('失败') || e.message?.includes('识别')) throw e;
              }
            }
          }
        }
      } else {
        let detail = `HTTP ${response.status}`;
        try { const err = await response.json(); detail = err.detail || detail; } catch {}
        throw new Error(`标段识别失败: ${detail}`);
      }

      if (!gotResult) throw new Error('未能获取标段识别结果');

      setPackages(pkgList);
      setPackagesChecked(true);
      setAnalyzing(false);
      setAnalysisProgress('');

      if (pkgList.length === 0) {
        setWizardStep('form');
      } else {
        // 都显示标段选择界面（含1个标段时也让用户看到missing_attachments提示）
        setWizardStep('form');
      }
    } catch (error: any) {
      setAnalyzing(false);
      setAnalysisProgress('');
      toast.error(`标段识别失败: ${error.message}`);
    }
  };

  /** 第二步：调 analyze-project-info 提取项目信息（后端已解析为结构化结果） */
  const fetchProjectInfo = async (fileContent: string, pkg: any | null) => {
    setAnalyzing(true);
    setAnalysisProgress(`正在分析${pkg ? '标段「' + pkg.name + '」' : ''}项目信息...`);

    let retryCount = 0;
    const maxRetries = 2;

    while (retryCount <= maxRetries) {
      try {
        // 后端非流式返回 { result: <对象|数组> }，已做 JSON/Markdown 兜底解析
        const res = await api.post(
          '/api/document/analyze-project-info',
          {
            file_content: fileContent,
            selected_package: pkg,
            document_type: documentType,
          },
          { timeout: 300000 },
        );

        const result = res.data?.result;
        // 有 selected_package 时期望单个对象；无时期望数组（取首个元素）
        let projectData;
        if (pkg) {
          projectData = result;
        } else if (Array.isArray(result)) {
          projectData = result.length > 0 ? result[0] : null;
        } else {
          projectData = result;
        }

        if (!projectData || !projectData.project_name) {
          throw new Error(
            Array.isArray(result) && result.length === 0
              ? '未识别到标段信息，文档可能不包含独立标段。请尝试选择标段后分析，或手动填写项目信息。'
              : '未能提取到有效的项目信息',
          );
        }

        fillFormWithPackage(projectData);
        setAnalyzing(false);
        setAnalysisProgress('');
        setWizardStep('form');
        return; // 成功
      } catch (error: any) {
        const currentRetry = retryCount + 1;
        // axios 错误优先取后端 detail（BusinessException 抛出的 message）
        const errMsg = error?.response?.data?.detail || error?.message || '未知错误';
        console.error(`分析失败 (尝试 ${currentRetry}/${maxRetries + 1}):`, errMsg);

        const transient =
          /网络|连接|timeout|Failed to fetch|Network Error|ECONN/i.test(errMsg) ||
          error?.code === 'ECONNABORTED';
        if (retryCount < maxRetries && transient) {
          retryCount++;
          setAnalysisProgress(`连接中断，正在重试 (${retryCount}/${maxRetries})...`);
          const delay = 2000 * retryCount;
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        toast.warning(`自动分析失败: ${errMsg}。请手动填写基本信息。`);
        setAnalyzing(false);
        setAnalysisProgress('');
        setWizardStep('form');
        return;
      }
    }
  };

  /** 用户选择标段后确认 */
  const handleSelectPackage = async (pkg: any) => {
    setSelectedPackage(pkg);
    await fetchProjectInfo(formData.file_content, pkg);
  };

  /** 跳过标段识别，整份分析 */
  const handleNoPackage = async () => {
    setSelectedPackage(null);
    await fetchProjectInfo(formData.file_content, null);
  };

  /** 从分析中返回标段选择 */
  const handleBackToPackageSelect = () => {
    setWizardStep('form');
    setAnalyzing(false);
    setAnalysisProgress('');
  };

  /** 从表单回到上传 */
  const handleBackToUpload = () => {
    setWizardStep('form');
    setPackages([]);
    setPackagesChecked(false);
    setSelectedPackage(null);
    setFormData(prev => ({
      ...prev,
      project_name: '',
      project_overview: '',
      tech_requirements: '',
      business_requirements: '',
      price_requirements: '',
    }));
  };

  const handleSubmit = async () => {
    if (!formData.project_name.trim()) { toast.warning('请输入项目名称'); return; }
    if (!formData.file_content) { toast.warning('请先上传文件'); return; }

    try {
      setLoading(true);
      const response = await projectApi.createProject({ ...formData, document_type: documentType, proposal_mode: documentType === 'proposal' ? proposalMode : 'normal' });
      navigate(`/projects/${response.data.id}/wizard`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '创建项目失败');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground";
  const textareaCls = "w-full text-sm bg-white border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all placeholder-muted-foreground resize-none";
  const autoGrow = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  return (
    <div className="max-w-4xl mx-auto py-4">
      <PageHeader title="新建项目" />
      <p className="text-xs text-muted-foreground mb-6">{documentType === 'bid' ? '上传招标文件自动分析，或直接手动填写项目信息' : '上传需求文档自动提取，或直接手动填写项目信息'}</p>

      {/* ====================== 选择文档类型 ====================== */}
      {wizardStep === 'select_type' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => { setDocumentType('bid'); setWizardStep('form'); }}
            className="bg-white rounded-2xl border-2 border-border hover:border-primary p-8 text-left transition-all hover:shadow-md"
          >
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">技术标书</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              上传招标文件，自动识别标段、提取评分要求，生成符合评分标准的标书内容
            </p>
          </button>
          <button
            onClick={() => { setDocumentType('proposal'); setWizardStep('form'); }}
            className="bg-white rounded-2xl border-2 border-border hover:border-primary p-8 text-left transition-all hover:shadow-md"
          >
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">方案</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              上传需求文档，自动提取关键信息，生成完整的解决方案文档
            </p>
          </button>
        </div>
      )}

      {/* ====================== 上传文件卡片 ====================== */}
      {wizardStep === 'form' && (
      <div className="bg-white rounded-2xl border border-border mb-4 overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-3 bg-accent border-b border-border">
          <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
          <h2 className="text-sm font-bold text-foreground">{documentType === 'bid' ? '上传招标文件' : '上传需求文件'}</h2>
        </div>
        <div className="p-6">
          <label htmlFor="fu" className="border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors block">
            {uploadedFiles.length > 0 && (
              <div className="space-y-2 mb-5">
                {uploadedFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-accent rounded-xl px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      <span className="text-sm text-foreground">{f.name}</span>
                      <span className="text-xs text-muted-foreground">{(f.size/1024).toFixed(1)}KB</span>
                    </div>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFile(i); }} disabled={uploading || analyzing} className="text-muted-foreground hover:text-red-500">&times;</button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col items-center gap-3 group">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/10 transition-colors"><svg className="w-7 h-7 text-primary group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg></div>
              <div><label htmlFor="fu" className="cursor-pointer text-sm"><span className="text-primary font-medium hover:underline">点击选择文件</span></label>
              <input id="fu" type="file" className="hidden" accept={DOCUMENT_ACCEPT} multiple onChange={handleFileSelect} disabled={uploading||analyzing} /></div>
              <p className="text-xs text-muted-foreground">支持 PDF、Word、TXT、Markdown 等格式，单文件不超过 100MB。如需添加更多文件，在此追加后点"开始分析"即可。</p>
              {uploading ? (
                <p className="text-sm text-primary">上传中（{uploadingIndex+1}/{uploadedFiles.length}）...</p>
              ) : uploadedFiles.length > 0 ? (
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUpload(); }} disabled={analyzing}
                  className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-[0_4px_12px_rgba(0,82,217,0.2)] disabled:opacity-50">
                  {analyzing ? '分析中...' : '开始分析'}
                </button>
              ) : null}
            </div>
          </label>

          {!analyzing && formData.project_overview && (
            <p className="mt-3 text-xs text-green-600"><CheckCircleIcon className="w-5 h-5 inline-block" /> 已自动分析并填充项目信息</p>
          )}
        </div>
      </div>
      )}

      {/* ====================== 附件上传区域 ====================== */}
        {packagesChecked && packages.some((pkg: any) => pkg.missing_attachments?.length > 0) && (
          <div className="mt-4">
            <p className="text-sm font-medium text-foreground mb-2">上传附件文件：</p>
            <div className="flex gap-2 flex-wrap">
              <label className="px-4 py-2 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-blue-400 text-sm text-muted-foreground flex items-center gap-1">
                <PlusIcon className="w-4 h-4" /> 添加附件
                <input type="file" accept={DOCUMENT_ACCEPT} multiple className="hidden"
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files) return;
                    for (const file of Array.from(files)) {
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        const res = await api.post('/api/document/upload', fd, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        if (res.data.success) {
                          // Append attachment content to file_content
                          setFormData(prev => ({
                            ...prev,
                            file_content: prev.file_content + '\n\n---\n【附件：' + file.name + '】\n' + res.data.file_content
                          }));
                        }
                      } catch { /* ignore individual file errors */ }
                    }
                    toast.success('附件已添加');
                  }}
                />
              </label>
            </div>
          </div>
        )}

      {/* ====================== 标段选择区域 ====================== */}
      {!analyzing && packagesChecked && (
        <div className="bg-white rounded-2xl border border-border mb-4 overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-3 bg-accent border-b border-border">
            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
            <h2 className="text-sm font-bold text-foreground">
              {packages.length === 0 ? '标段识别结果' : '选择投标标段'}
            </h2>
          </div>
          <div className="p-6">
            {packages.length === 0 ? (
              /* ── 未识别到标段 ── */
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-amber-50 flex items-center justify-center">
                  <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/></svg>
                </div>
                <p className="text-sm font-medium text-foreground mb-1">未识别到标段</p>
                <p className="text-xs text-muted-foreground mb-4">AI 未能从文件中识别出独立的投标标段信息</p>
                <button onClick={handleNoPackage}
                  className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-[0_4px_12px_rgba(0,82,217,0.2)]">
                  跳过，整份分析
                </button>
              </div>
            ) : (
              /* ── 标段卡片列表 ── */
              <div>
                <p className="text-xs text-muted-foreground mb-4">请选择要投标的标段：</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                  {packages.map((pkg, idx) => {
                    const selected = selectedPackage?.id === pkg.id;
                    return (
                      <div
                        key={idx}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${
                          selected
                            ? 'border-primary bg-primary/10 shadow-[0_0_0_1px_#0052d9]'
                            : 'border-border bg-white hover:border-primary/30 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selected ? 'border-primary bg-primary' : 'border-border'
                          }`}>
                            {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground truncate">{pkg.name}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{pkg.id}</div>
                            {pkg.desc && (
                              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{pkg.desc}</div>
                            )}
                            {pkg.missing_attachments && pkg.missing_attachments.length > 0 ? (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {pkg.missing_attachments.map((att: string, ai: number) => (
                                  <span key={ai} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded">
                                    <ExclamationTriangleIcon className="w-5 h-5 inline-block" /> {att}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="inline-block mt-1.5 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded"><CheckCircleIcon className="w-5 h-5 inline-block" /> 附件完整</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => selectedPackage && handleSelectPackage(selectedPackage)}
                    disabled={!selectedPackage}
                    className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-[0_4px_12px_rgba(0,82,217,0.2)] disabled:opacity-50 disabled:cursor-not-allowed">
                    {selectedPackage
                      ? `确认选择 → 分析「${selectedPackage.name}」`
                      : '请先选择一个标段'}
                  </button>
                  <button onClick={handleNoPackage}
                    className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground font-medium">
                    跳过，整份分析
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====================== 附件引用提示 ====================== */}
      {packages.some((pkg: any) => pkg.missing_attachments?.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">检测到外部附件引用</p>
              <p className="text-xs text-amber-600 mb-2">
                招标文件中引用了以下附件，上传这些附件可提升分析完整度：
              </p>
              <div className="flex flex-wrap gap-2">
                {Array.from(new Set(
                  packages.flatMap((pkg: any) => pkg.missing_attachments || [])
                )).map((att: string, i: number) => (
                  <span key={i} className="px-2 py-1 text-xs bg-white border border-amber-300 rounded-md text-amber-700">
                    {att}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================== 分析进度 ====================== */}
      {analyzing && (
        <div className="bg-white rounded-2xl border border-border mb-4 overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-3 bg-accent border-b border-border">
            <svg className="w-4 h-4 text-primary animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            <h2 className="text-sm font-bold text-foreground">正在分析文档</h2>
          </div>
          <div className="p-6 text-center">
            <p className="text-sm text-primary mb-3">{analysisProgress}</p>
          </div>
        </div>
      )}

      {/* ====================== 项目信息表单 ====================== */}
      {wizardStep === 'form' && (
        <>
          {/* 已选包标签 */}
          {selectedPackage && (
            <div className="bg-primary/10 rounded-xl border border-primary/20 px-4 py-3 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                <span className="text-sm font-medium text-foreground">
                  已选标段：{selectedPackage.name}（{selectedPackage.id}）
                </span>
              </div>
              <button onClick={handleBackToUpload} className="text-xs text-primary hover:underline">重新选择</button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-border mb-4 overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-3 bg-accent border-b border-border">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7"/></svg>
              <h2 className="text-sm font-bold text-foreground">项目信息</h2>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-xs font-medium text-muted-foreground mb-1">项目名称 <span className="text-red-400">*</span></label>
                <input type="text" value={formData.project_name} onChange={e=>setFormData({...formData,project_name:e.target.value})} placeholder="请输入项目名称" className={inputCls} /></div>
              <div><label className="block text-xs font-medium text-muted-foreground mb-1">项目概述</label>
                <textarea value={formData.project_overview} onChange={e=>setFormData({...formData,project_overview:e.target.value})} onInput={autoGrow} placeholder="项目基本信息、背景、规模、时间安排、实施内容等" rows={10} className={textareaCls} /></div>

              {documentType === 'bid' && (
              <>
              <div className="flex items-center gap-2 px-4 py-2 bg-accent rounded-xl -mx-1">
                <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg><span className="text-xs font-bold text-foreground">评分项</span></div>
              <div><label className="block text-xs font-medium text-muted-foreground mb-1">技术评分项 <span className="text-red-400">*</span></label>
                <textarea value={formData.tech_requirements} onChange={e=>setFormData({...formData,tech_requirements:e.target.value})} onInput={autoGrow} placeholder="技术评分项名称、权重/分值、评分标准" rows={4} className={textareaCls} /></div>
              <div><label className="block text-xs font-medium text-muted-foreground mb-1">商务评分项</label>
                <textarea value={formData.business_requirements} onChange={e=>setFormData({...formData,business_requirements:e.target.value})} onInput={autoGrow} placeholder="商务评分项（资质、业绩、人员等）" rows={3} className={textareaCls} /></div>
              <div><label className="block text-xs font-medium text-muted-foreground mb-1">价格评分项</label>
                <textarea value={formData.price_requirements} onChange={e=>setFormData({...formData,price_requirements:e.target.value})} onInput={autoGrow} placeholder="价格评分项（计算方式、权重等）" rows={3} className={textareaCls} /></div>
              </>
              )}
              {documentType === 'proposal' && (
              <>
              <div className="flex items-center gap-2 px-4 py-2 bg-accent rounded-xl -mx-1">
                <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg><span className="text-xs font-bold text-emerald-600">需求分析</span></div>
              <div><label className="block text-xs font-medium text-muted-foreground mb-1">业务需求分析 <span className="text-red-400">*</span></label>
                <textarea value={formData.business_requirements} onChange={e=>setFormData({...formData,business_requirements:e.target.value})} onInput={autoGrow} placeholder="按业务域整理的核心需求、痛点、优先级，如：&#10;【需求领域】：信息中心&#10;【需求名称】：统一数据中台&#10;【优先级】：P0&#10;【关联业务】：全校数据服务" rows={8} className={textareaCls} /></div>
              </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border overflow-hidden mb-4">
            <div className="flex items-center gap-2 px-6 py-3 bg-accent border-b border-border">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <h2 className="text-sm font-bold text-foreground">配置与创建</h2>
            </div>
            <div className="p-6">
              <div className="mb-4 space-y-3">
                {documentType === 'proposal' && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">方案模式</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setProposalMode('normal')}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${proposalMode === 'normal' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      <div className="text-sm font-bold text-foreground">正常方案</div>
                      <div className="mt-1 text-xs text-muted-foreground leading-relaxed">按现有模式生成完整方案文档</div>
                    </button>
                    <button type="button" onClick={() => setProposalMode('concise')}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${proposalMode === 'concise' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      <div className="text-sm font-bold text-foreground">精简方案</div>
                      <div className="mt-1 text-xs text-muted-foreground leading-relaxed">不超过5页纸的领导汇报稿：做什么、怎么做、有什么效益</div>
                    </button>
                  </div>
                  {proposalMode === 'concise' && (
                    <p className="mt-1.5 text-xs text-emerald-600">生成≤5页纸的领导汇报材料：每章2~3段、只写领导关注的重点，不受上方预期字数影响</p>
                  )}
                </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">项目金额（万元）</label>
                  <input
                    type="number"
                    placeholder="如 500（即500万元）"
                    value={formData.project_amount || ''}
                    onChange={e => {
                      const amount = +e.target.value;
                      // 超过500万的项目：每10万对应1万字
                      const calculated = amount >= 500 ? amount * 1000 : (formData.expected_word_count >= 300000 ? formData.expected_word_count : 300000);
                      setFormData({ ...formData, project_amount: amount, expected_word_count: calculated });
                    }}
                    className={inputCls}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">超过500万的项目，按项目金额自动计算预期字数</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">预期字数（字）</label>
                  <input
                    type="number"
                    value={formData.expected_word_count}
                    onChange={e => setFormData({ ...formData, expected_word_count: +e.target.value })}
                    className={inputCls}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">默认30万字，可手动调整</p>
                </div>
              </div>
              {models.length === 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                  未配置模型，<button onClick={()=>navigate('/models')} className="text-primary hover:underline">立即配置</button>
                </div>
              )}
              <button onClick={handleSubmit} disabled={loading||!formData.project_name.trim()}
                className="w-full py-3 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-[0_4px_12px_rgba(0,82,217,0.2)] disabled:opacity-50 active:scale-[0.98] transition-all">
                {loading ? '创建中...' : '创建项目'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectWizard;
